import IconButton from '@components/v2/ui/IconButton/IconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Collapse, Stack } from '@mui/material';
import { ChevronDown, ChevronUp, Ellipsis } from 'lucide-react';
import { ReactNode } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../../modules/translation/TranslationContext';
import { useDetailsPanelDisplay } from '../../hooks/useDetailsPanelDisplay';
import { ObjectLabel } from '../../objectScreen.types';
import { useDetailsSection } from './hooks/useDetailsSection';

interface DetailsSectionProps {
  title: ObjectLabel;
  isDefaultOpen?: boolean;
  persistKey?: string;
  testId?: string;
  alert?: ReactNode;
  info?: ReactNode;
  action?: ReactNode;
  onMoreActions?: () => void;
  children: ReactNode;
}

export function DetailsSection({
  title,
  isDefaultOpen = false,
  persistKey,
  testId,
  alert,
  info,
  action,
  onMoreActions,
  children,
}: DetailsSectionProps) {
  const { translate } = useTranslation();
  const { hasBackdrop } = useDetailsPanelDisplay();
  const { isOpen, toggle } = useDetailsSection({ title, isDefaultOpen, persistKey });

  return (
    <Box
      test-id={testId}
      data-details-section=""
      sx={(theme) => ({
        border: 1,
        borderRadius: `${theme.shape.borderRadiusXl}px`,
        borderColor: theme.palette.custom.border.secondary,
        bgcolor: 'background.paper',
        boxShadow: hasBackdrop ? theme.shadows[1] : 'none',
        flexShrink: 0,
        overflow: 'hidden',
      })}
    >
      <Stack
        direction="row"
        data-details-section-header=""
        sx={{ alignItems: 'center', gap: 0.5, pl: 1.5, pr: 0.75, py: 0.75 }}
      >
        <Stack
          direction="row"
          onClick={toggle}
          sx={{ alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0, cursor: 'pointer' }}
        >
          {alert}
          <Typography variant="body2" noWrap sx={{ color: 'text.primary' }}>
            {translate(title as LangEnum)}
          </Typography>
          {info}
        </Stack>
        {action}
        {onMoreActions && (
          <IconButton
            icon={Ellipsis}
            size="medium"
            ariaLabel={translate(LangEnum.GLOBAL_MORE_ACTIONS)}
            onClick={onMoreActions}
          />
        )}
        <IconButton
          icon={isOpen ? ChevronUp : ChevronDown}
          size="medium"
          ariaLabel={translate(LangEnum.GLOBAL_SECTION_TOGGLE)}
          onClick={toggle}
        />
      </Stack>
      <Collapse in={isOpen}>
        <Stack sx={{ gap: 0.75, px: 1.5, pt: 0.75, pb: 1.5 }}>{children}</Stack>
      </Collapse>
    </Box>
  );
}
